class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-product-form]');
    if (!this.form) return;

    this.variantSelect = this.form.querySelector('[data-variant-select]');
    this.variantSelect?.classList.add('hidden');

    this.submitButton = this.form.querySelector('[type="submit"]');
    this.buyNowButton = this.querySelector('[data-buy-now]');
    this.errorContainer = this.querySelector('[data-form-error]');

    // Availability of the variant this form currently submits. The buttons'
    // disabled state and label are always derived from it (applyVariantState),
    // never read back from the page, where the server may have rendered
    // "Sold out" for a ?variant= link.
    this.variantState = this.submitButton?.disabled ? 'sold_out' : 'available';
    this.showingConfirmation = false;

    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.buyNowButton?.addEventListener('click', () => this.onBuyNow());
    this.initQuantityStepper();
    this.initVariantSubscriber();
  }

  disconnectedCallback() {
    this.unsubscribeVariant?.();
    clearTimeout(this.confirmationTimer);
  }

  initVariantSubscriber() {
    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
      if (data.productId !== this.dataset.productId) return;

      // Only forms in the same product section as the selector that fired
      // follow it (the PDP's main form and sticky bar). A card for the same
      // product elsewhere — the search drawer's fallback grid renders before
      // <main> — keeps its own variant and its icon-only button intact.
      const productSection = this.closest('[data-product-section]');
      if (!productSection || !data.source || !productSection.contains(data.source)) return;

      if (data.variant && this.variantSelect) this.variantSelect.value = data.variant.id;

      if (!data.variant) {
        this.variantState = 'unavailable';
      } else {
        this.variantState = data.variant.available ? 'available' : 'sold_out';
      }
      this.applyVariantState();
    });
  }

  applyVariantState() {
    const labels = {
      sold_out: window.theme?.strings?.soldOut ?? 'Sold out',
      unavailable: window.theme?.strings?.unavailable ?? 'Unavailable',
    };

    for (const button of [this.submitButton, this.buyNowButton]) {
      if (!button) continue;
      button.disabled = this.variantState !== 'available';

      const buttonText = button.querySelector('[data-button-text]');
      // Icon-only (compact) buttons have no text label to swap, and the
      // "Added to cart" confirmation owns the text until its timer ends.
      if (!buttonText || button.querySelector('[data-button-plus-icon]')) continue;
      if (button === this.submitButton && this.showingConfirmation) continue;

      buttonText.textContent =
        this.variantState === 'available' ? buttonText.dataset.availableLabel ?? buttonText.textContent : labels[this.variantState];
    }
  }

  initQuantityStepper() {
    this.addEventListener('click', (event) => {
      const minusBtn = event.target.closest('[data-quantity-minus]');
      const plusBtn = event.target.closest('[data-quantity-plus]');

      if (!minusBtn && !plusBtn) return;

      const input = this.querySelector('input[name="quantity"]');
      if (!input) return;

      const currentQty = parseInt(input.value, 10) || 1;
      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || 99;

      if (minusBtn) {
        input.value = Math.max(min, currentQty - 1);
      } else if (plusBtn) {
        input.value = Math.min(max, currentQty + 1);
      }
    });
  }

  onBuyNow() {
    if (this.buyNowButton?.disabled) return;

    const formData = new FormData(this.form);
    const variantId = parseInt(formData.get('id'), 10);
    const quantity = parseInt(formData.get('quantity'), 10) || 1;
    if (!variantId) return;

    window.location.href = `${CartAPI.getRoot()}cart/${variantId}:${quantity}`;
  }

  async onSubmit(event) {
    event.preventDefault();

    if (this.submitButton?.disabled) return;
    this.setLoading(true);
    this.hideError();

    const cartDrawer = document.querySelector('cart-drawer');
    const sectionId = cartDrawer?.dataset?.sectionId;
    const sections = sectionId ? [sectionId] : [];
    const formData = new FormData(this.form);
    const variantId = parseInt(formData.get('id'), 10);

    await CartStore.init();

    const wasEmpty = (CartStore.state?.item_count ?? 0) === 0;

    const emptyEl = cartDrawer?.querySelector('[data-cart-empty]');
    const itemsEl = cartDrawer?.querySelector('[data-cart-items]');
    const footerEl = cartDrawer?.querySelector('[data-cart-footer]');
    const skeletonEl = cartDrawer?.querySelector('[data-cart-skeleton]');
    if (emptyEl) emptyEl.hidden = true;

    if (footerEl) footerEl.hidden = false;

    let shimmerEl = null;
    let optimisticEl = null;

    const existingItem = wasEmpty
      ? null
      : CartStore.state?.items?.find((item) => item.variant_id === variantId);

    if (existingItem) {
      const row = cartDrawer?.querySelector(`[data-item-key="${existingItem.key}"]`);
      const parent = row?.parentElement;
      shimmerEl = (parent?.children.length === 1 ? parent : row) ?? null;
      shimmerEl?.classList.add('animate-pulse');
    } else {
      if (wasEmpty && itemsEl) itemsEl.hidden = false;

      optimisticEl = this.buildOptimisticLineItem(cartDrawer, variantId);
      if (optimisticEl && itemsEl) {
        if (skeletonEl) skeletonEl.hidden = true;
        optimisticEl.classList.add('animate-pulse');
        itemsEl.prepend(optimisticEl);
      } else if (skeletonEl) {
        skeletonEl.hidden = false;
      }
    }

    CartStore.addPending();

    try {
      const data = await CartAPI.add(formData, sections);

      optimisticEl?.remove();

      if (sectionId && data.sections?.[sectionId] && cartDrawer) {
        const doc = new DOMParser().parseFromString(data.sections[sectionId], 'text/html');

        const newItemsEl = doc.querySelector('[data-cart-items]');
        const existingItemsEl = cartDrawer.querySelector('[data-cart-items]');
        if (newItemsEl && existingItemsEl) {
          Idiomorph.morph(existingItemsEl, newItemsEl, { morphStyle: 'outerHTML' });
        }

        const newFooterEl = doc.querySelector('[data-cart-footer]');
        const existingFooterEl = cartDrawer.querySelector('[data-cart-footer]');
        if (newFooterEl && existingFooterEl) {
          Idiomorph.morph(existingFooterEl, newFooterEl, { morphStyle: 'outerHTML' });
        }
      }

      // The add itself has succeeded at this point. If only this follow-up
      // refresh fails, the item is still in the cart: keep the optimistic
      // count rather than falling into the catch below, which would roll the
      // count back and report an error for an add that worked.
      let cartData = null;
      try {
        cartData = await CartAPI.get();
      } catch {
        cartData = null;
      }
      CartStore.addConfirm(cartData ?? CartStore.state);
      this.showAddedConfirmation();

      if (this.form.dataset.buyNow === 'true') {
        delete this.form.dataset.buyNow;
        window.location.href = `${CartAPI.getRoot()}checkout`;
        return;
      }
    } catch (error) {
      CartStore.addRollback();
      const message = error?.description ?? error?.message ?? window.theme?.strings?.cartError ?? 'Something went wrong.';
      if (cartDrawer) {
        cartDrawer.showError(message);
      } else {
        this.showError(message);
      }
    } finally {
      this.setLoading(false);
      const finalSkeletonEl = cartDrawer?.querySelector('[data-cart-skeleton]');
      if (finalSkeletonEl) finalSkeletonEl.hidden = true;
      shimmerEl?.classList.remove('animate-pulse');
      optimisticEl?.remove();
    }
  }

  getVariantData(variantId) {
    const selector = document.querySelector(
      `variant-selector[data-product-id="${this.dataset.productId}"]`
    );
    const variants = selector?.variants;
    if (!Array.isArray(variants)) return null;
    return variants.find((variant) => variant.id === variantId) ?? null;
  }

  buildOptimisticLineItem(cartDrawer, variantId) {
    const template = cartDrawer?.querySelector('[data-cart-line-optimistic]');
    const root = template?.content?.firstElementChild;
    if (!root) return null;

    const { productTitle, productUrl, productFeaturedImage, optimisticPrice } = this.dataset;


    const variant = this.getVariantData(variantId);
    const imageSrc = variant?.image || productFeaturedImage || '';
    const variantTitle =
      variant?.title && variant.title !== 'Default Title' ? variant.title : '';
    const priceText = variant?.price_formatted || optimisticPrice || '';

    if (!productTitle && !imageSrc && !priceText) return null;

    const node = root.cloneNode(true);

    const imageEl = node.querySelector('[data-optimistic-image]');
    if (imageEl) {
      if (imageSrc) {
        imageEl.src = imageSrc;
        imageEl.alt = productTitle ?? '';
      } else {
        imageEl.closest('[data-optimistic-link]')?.remove();
      }
    }

    const titleEl = node.querySelector('[data-optimistic-title]');
    if (titleEl) titleEl.textContent = productTitle ?? '';

    const variantEl = node.querySelector('[data-optimistic-variant]');
    if (variantEl) {
      if (variantTitle) {
        variantEl.textContent = variantTitle;
      } else {
        variantEl.remove();
      }
    }

    const priceEl = node.querySelector('[data-optimistic-price]');
    if (priceEl) priceEl.textContent = priceText;

    for (const link of node.querySelectorAll('[data-optimistic-link]')) {
      if (productUrl) link.href = productUrl;
    }

    return node;
  }

  showAddedConfirmation() {
    if (!this.submitButton) return;

    clearTimeout(this.confirmationTimer);

    const checkmarkIcon = this.submitButton.querySelector('[data-button-checkmark-icon]');
    const plusIcon = this.submitButton.querySelector('[data-button-plus-icon]');
    const buttonText = this.submitButton.querySelector('[data-button-text]');

    // Compact (icon-only) button: swap the icon and solidify the fill so the
    // confirmation reads clearly even where the button's resting state is a
    // translucent outline (touch devices have no hover state to darken it).
    if (checkmarkIcon && plusIcon) {
      plusIcon.classList.add('hidden');
      checkmarkIcon.classList.remove('hidden');
      this.submitButton.classList.add('is-added');
    } else if (this.submitButton.hasAttribute('data-add-to-bag') && document.querySelector('ts-cart-drawer')) {
      // PDP Add to bag with the TS drawer: the drawer opening on add is the
      // confirmation, and the longer "added" text wraps in this half-width
      // button on mobile.
      return;
    } else if (buttonText) {
      // Full-width PDP button already renders solid — text is the only
      // confirmation it needs.
      this.showingConfirmation = true;
      buttonText.textContent = window.theme?.strings?.addedToCart ?? 'Added to cart';
    }

    this.confirmationTimer = setTimeout(() => {
      if (checkmarkIcon && plusIcon) {
        checkmarkIcon.classList.add('hidden');
        plusIcon.classList.remove('hidden');
        this.submitButton.classList.remove('is-added');
      } else {
        // Re-derived rather than restoring the text saved 2s ago — the
        // variant may have changed during the confirmation.
        this.showingConfirmation = false;
        this.applyVariantState();
      }
    }, 2000);
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    // Ending a load re-derives the enabled state from the current variant
    // instead of simply re-enabling — it may have become sold out meanwhile.
    if (isLoading) {
      this.submitButton.disabled = true;
    } else {
      this.applyVariantState();
    }

    // Undoes disabled:opacity-50's fade during this transient state — see
    // assets/base.css for why that's otherwise needed.
    this.submitButton.classList.toggle('is-loading', isLoading);

    const buttonText = this.submitButton.querySelector('[data-button-text]');
    const buttonLoader = this.submitButton.querySelector('[data-button-loader]');

    // Must be visibility:hidden (invisible), not display:none (hidden): the
    // loader div is absolutely positioned and contributes nothing to the
    // button's own size, so buttonText is the only thing still in flow that
    // gives the button its dimensions. display:none on it collapses the
    // whole button to a padding-only box mid-load. invisible keeps the
    // layout space reserved while hiding the icon. It's safelisted in
    // tailwind.config.js since this class only ever appears here, never in
    // a .liquid file Tailwind's content scan would pick up.
    if (buttonText) buttonText.classList.toggle('invisible', isLoading);
    if (buttonLoader) {
      buttonLoader.classList.toggle('hidden', !isLoading);
      buttonLoader.classList.toggle('flex', isLoading);
    }
  }

  showError(message) {
    if (!this.errorContainer) return;
    this.errorContainer.textContent = message;
    this.errorContainer.hidden = false;
  }

  hideError() {
    if (!this.errorContainer) return;
    this.errorContainer.textContent = '';
    this.errorContainer.hidden = true;
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
